'use client'

/**
 * Calculadora de cuánto deja el Ratón Pérez, para la guía `cuanto-deja-…`.
 *
 * Esa guía no tenía nada que hacer: la respuesta genérica está en la primera
 * línea, así que la intención quedaba satisfecha y el visitante se iba. Aquí la
 * cifra pasa a ser la del caso concreto (país, qué diente, si tuvo mérito) y,
 * sobre todo, el padre se va con la notita que acompaña la moneda.
 *
 * Donde no tenemos base real para una cifra, lo decimos en vez de inventarla
 * (ver `suggestToothMoney`). La notita se genera igual: es lo que de verdad se
 * lleva, y funciona para cualquier país.
 */

import { useState } from 'react'
import { Coins } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ComposedLetter, ToothKind } from '@/lib/guides/tools/tooth-letter'
import {
  COHERENCE_ADVICE,
  COUNTRIES,
  COUNTRY_ORDER,
  composeToothNote,
  suggestToothMoney,
  type CountryKey,
  type MoneySuggestion,
} from '@/lib/guides/tools/tooth-money'
import { captureProductEvent, PRODUCT_EVENTS } from '@/lib/product-analytics'
import { ComposedLetterSheet } from './composed-letter-sheet'
import { CallBridge } from './call-bridge'
import { ToothPicker } from './tooth-letter-generator'

const COPY = {
  heading: '¿Cuánto dejo yo?',
  intro:
    'Dinos el caso y te damos la referencia que aplica, además de la notita del ratón para dejar con la moneda.',
  country: '¿Dónde vivís?',
  tooth: '¿Qué diente es?',
  merit: 'Tuvo mérito extra (dentista, un golpe, mucho miedo)',
  name: 'Nombre del niño o la niña (para la notita)',
  namePlaceholder: 'Lucía',
  amount: '¿Cuánto le vas a dejar? (opcional, va en la notita)',
  amountPlaceholder: '2 €',
  calculate: 'Ver la referencia',
  recalculate: 'Actualizar',
  suggestionLabel: 'Referencia orientativa',
  noFigure: 'Sin referencia fiable',
  disclaimer:
    'Son cifras orientativas: la cantidad la decide cada familia, y el Ratón Pérez está de acuerdo con todas.',
  noteHint: 'Y la notita para dejar junto a la moneda:',
  nameForNote: 'Pon el nombre y te generamos también la notita del ratón.',
  sheetTitle: 'Notita del Ratón Pérez',
  filename: 'notita-del-raton-perez.png',
  ctaTitle: '¿Y si {name} lo oye del propio ratón?',
  ctaBody:
    'El Ratón Pérez puede llamarle esta noche por videollamada: le felicita por su diente, le llama por su nombre y convierte el ritual en un recuerdo.',
  ctaAction: 'Preparar la llamada del Ratón Pérez',
}

const ANALYTICS_BASE = {
  character_id: 'raton-perez',
  guide_slug: 'cuanto-deja-el-raton-perez',
  locale: 'es',
}

function SuggestionBlock({ suggestion }: { suggestion: MoneySuggestion }) {
  if (suggestion.kind === 'ask-around') {
    return (
      <div className="rounded-2xl border border-white/15 bg-white/5 p-5">
        <p className="text-sm font-semibold uppercase tracking-wide text-white/60">
          {COPY.noFigure}
        </p>
        <p className="mt-2 text-base text-white/85">{suggestion.reason}</p>
      </div>
    )
  }
  return (
    <div className="rounded-2xl border border-white/15 bg-white/5 p-5">
      <p className="text-sm font-semibold uppercase tracking-wide text-white/60">
        {COPY.suggestionLabel}
      </p>
      <p className="mt-1 text-3xl font-bold text-white">{suggestion.formatted}</p>
      <p className="mt-3 text-sm text-white/70">{COPY.disclaimer}</p>
    </div>
  )
}

type FormState = {
  country: CountryKey
  tooth: ToothKind
  hasMerit: boolean
  childName: string
  amount: string
}

const EMPTY_FORM: FormState = {
  country: 'es',
  tooth: 'first',
  hasMerit: false,
  childName: '',
  amount: '',
}

type Result = { suggestion: MoneySuggestion; note: ComposedLetter | null; name: string }

/** Selector de país como píldoras, igual que el de diente. */
function CountryPicker({
  value,
  onChange,
}: {
  value: CountryKey
  onChange: (country: CountryKey) => void
}) {
  return (
    <fieldset>
      <legend className="text-sm text-white/80">{COPY.country}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {COUNTRY_ORDER.map((key) => (
          <label
            key={key}
            className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
              value === key
                ? 'border-white bg-white text-slate-900'
                : 'border-white/25 text-white/80 hover:border-white/50'
            }`}
          >
            <input
              type="radio"
              name="money-country"
              value={key}
              checked={value === key}
              onChange={() => onChange(key)}
              className="sr-only"
            />
            {COUNTRIES[key].label}
          </label>
        ))}
      </div>
    </fieldset>
  )
}

function MoneyForm({
  form,
  onChange,
  onSubmit,
  hasResult,
}: {
  form: FormState
  onChange: (patch: Partial<FormState>) => void
  onSubmit: (event: React.FormEvent) => void
  hasResult: boolean
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-4 print:hidden">
      <CountryPicker value={form.country} onChange={(country) => onChange({ country })} />
      <ToothPicker
        value={form.tooth}
        onChange={(tooth) => onChange({ tooth })}
        legend={COPY.tooth}
        name="money-tooth"
      />

      <label className="flex max-w-xl items-start gap-3 text-sm text-white/85">
        <input
          type="checkbox"
          checked={form.hasMerit}
          onChange={(event) => onChange({ hasMerit: event.target.checked })}
          className="mt-1"
        />
        <span>{COPY.merit}</span>
      </label>

      <div>
        <Label htmlFor="money-child-name">{COPY.name}</Label>
        <Input
          id="money-child-name"
          value={form.childName}
          onChange={(event) => onChange({ childName: event.target.value })}
          placeholder={COPY.namePlaceholder}
          autoComplete="off"
          className="mt-1"
        />
      </div>

      <div>
        <Label htmlFor="money-amount">{COPY.amount}</Label>
        <Input
          id="money-amount"
          value={form.amount}
          onChange={(event) => onChange({ amount: event.target.value })}
          placeholder={COPY.amountPlaceholder}
          autoComplete="off"
          className="mt-1"
        />
      </div>

      <Button
        type="submit"
        className="rounded-full bg-white px-6 py-5 text-base font-semibold text-slate-900 hover:bg-white/90"
      >
        <Coins className="size-4" aria-hidden />
        {hasResult ? COPY.recalculate : COPY.calculate}
      </Button>
    </form>
  )
}

function ResultBlock({ result }: { result: Result }) {
  return (
    <div className="space-y-4">
      <SuggestionBlock suggestion={result.suggestion} />
      <p className="text-base text-white/80 print:hidden">{COHERENCE_ADVICE}</p>

      {result.note ? (
        <>
          <p className="text-base text-white/80 print:hidden">{COPY.noteHint}</p>
          <ComposedLetterSheet
            letter={result.note}
            sheetTitle={COPY.sheetTitle}
            filename={COPY.filename}
            analyticsProperties={ANALYTICS_BASE}
          />
          <div className="print:hidden">
            <CallBridge
              childName={result.name}
              character="raton-perez"
              locale="es"
              title={COPY.ctaTitle}
              body={COPY.ctaBody}
              action={COPY.ctaAction}
              placement="money_calculator"
              analyticsProperties={ANALYTICS_BASE}
            />
          </div>
        </>
      ) : (
        <p className="text-sm text-white/60 print:hidden">{COPY.nameForNote}</p>
      )}
    </div>
  )
}

export function ToothMoneyCalculator() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [result, setResult] = useState<Result | null>(null)

  function handleChange(patch: Partial<FormState>) {
    setForm((current) => ({ ...current, ...patch }))
  }

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault()
    const suggestion = suggestToothMoney({
      country: form.country,
      tooth: form.tooth,
      hasMerit: form.hasMerit,
    })
    const name = form.childName.trim()
    // La cantidad de la notita la escribe el padre, NUNCA se rellena con la
    // referencia: el rango es orientación para él ("3-5 €"), y en la notita que
    // lee el niño un rango no tiene ningún sentido.
    setResult({
      suggestion,
      note: name ? composeToothNote({ childName: name, amount: form.amount }) : null,
      name,
    })
    captureProductEvent(PRODUCT_EVENTS.GUIDE_MONEY_CALCULATED, {
      ...ANALYTICS_BASE,
      country: form.country,
      tooth: form.tooth,
      has_merit: form.hasMerit,
      has_figure: suggestion.kind === 'range',
      generated_note: Boolean(name),
      has_amount: Boolean(form.amount.trim()),
    })
  }

  return (
    <div
      data-testid="tooth-money-calculator"
      className="space-y-5 rounded-3xl border border-white/15 bg-white/5 p-6 md:p-8"
    >
      <div className="print:hidden">
        <h3 className="font-christmas text-xl font-bold text-white md:text-2xl">{COPY.heading}</h3>
        <p className="mt-2 text-base text-white/80">{COPY.intro}</p>
      </div>

      <MoneyForm
        form={form}
        onChange={handleChange}
        onSubmit={handleSubmit}
        hasResult={result !== null}
      />

      {result && <ResultBlock result={result} />}
    </div>
  )
}
